import { Alert, Spinner, Table } from 'react-bootstrap';
import FinanceSearchBar from './FinanceSearchBar';
import FinanceFilterPanel from './FinanceFilterPanel';
import FinancePagination from './FinancePagination';
import FinanceRowCard from './FinanceRowCard';
import { useFinance } from '../financeContext';

// The shared shell for list pages 1–7: title + search + filter panel + Table/cards + pagination
// + empty/error states, all responsive (§A10). A page supplies its column config, the
// useFinanceQuery instance, and an optional per-row action renderer.
export default function FinanceListPage({
  title,
  subtitle,
  q,
  columns,
  rowKey = (r) => r._id,
  rowActions,
  filterFields = [],
  statusOptions = [],
  searchPlaceholder = 'Search…',
  emptyText = 'Nothing here.',
  notApplicable = false,
  notApplicableText
}) {
  const { scopedVertical } = useFinance();
  const showActions = typeof rowActions === 'function';
  // When scoped to one vertical, drop the "Vertical" filter and column — every row is that vertical.
  const effectiveFilterFields = scopedVertical ? filterFields.filter((f) => f !== 'vertical') : filterFields;
  const effectiveColumns = scopedVertical ? columns.filter((c) => c.key !== 'vertical') : columns;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-2">
        <h5 className="mb-0">{title}</h5>
        {q.pagination?.total != null && !q.syntheticPagination && (
          <span className="small text-muted">{q.pagination.total} total</span>
        )}
      </div>
      {subtitle && <p className="text-muted small">{subtitle}</p>}

      {notApplicable ? (
        <Alert variant="light">{notApplicableText || 'Not applicable to this vertical.'}</Alert>
      ) : (
        <div className="d-flex gap-3 align-items-start">
          {effectiveFilterFields.length > 0 && (
            <div className="order-lg-2">
              <FinanceFilterPanel fields={effectiveFilterFields} statusOptions={statusOptions} q={q} />
            </div>
          )}

          <div className="flex-grow-1 order-lg-1" style={{ minWidth: 0 }}>
            <div className="mb-3" style={{ maxWidth: 420 }}>
              <FinanceSearchBar value={q.qDraft} onChange={q.setQDraft} placeholder={searchPlaceholder} />
            </div>

            {q.error && <Alert variant="danger" dismissible onClose={() => {}}>{q.error}</Alert>}

            {q.loading ? (
              <Spinner animation="border" size="sm" role="status" />
            ) : q.rows.length === 0 ? (
              <Alert variant="light">{emptyText}</Alert>
            ) : (
              <>
                {/* >= md: table */}
                <div className="d-none d-md-block" style={{ overflowX: 'auto' }}>
                  <Table hover size="sm" className="align-middle">
                    <thead>
                      <tr>
                        {effectiveColumns.map((c) => <th key={c.key}>{c.header}</th>)}
                        {showActions && <th style={{ minWidth: 220 }}>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {q.rows.map((row) => (
                        <tr key={rowKey(row)}>
                          {effectiveColumns.map((c) => <td key={c.key}>{c.cell(row)}</td>)}
                          {showActions && <td>{rowActions(row)}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* xs–sm: cards */}
                <div className="d-md-none">
                  {q.rows.map((row) => (
                    <FinanceRowCard
                      key={rowKey(row)}
                      row={row}
                      columns={effectiveColumns}
                      actions={showActions ? rowActions(row) : null}
                    />
                  ))}
                </div>
              </>
            )}

            <FinancePagination
              page={q.page}
              totalPages={q.totalPages}
              limit={q.limit}
              hasMore={q.pagination?.hasMore}
              synthetic={q.syntheticPagination}
              onPage={q.setPage}
              onLimit={q.setLimit}
            />
          </div>
        </div>
      )}
    </div>
  );
}
